import {
  createContext,
  forwardRef,
  useLayoutEffect,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  ReactElement,
} from "react";
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_WIDTH_CLOSED } from "../../Components/Sidebar/sidebar";
import { Box, Breakpoint, Stack, styled, SxProps, Theme, Typography, useTheme } from "@mui/material";
import BasketPanel, { BASKETPANEL_WIDTH } from "../../Components/Basket/BasketPanel/basket-panel";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";

import React from "react";
import { MetadataFieldValueObjectTypes } from "../../Core/Api/Types/metadata-field-value-object-types";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { useDispatch, useSelector } from "react-redux";
import { GetSidebarOpen, ToggleSidebar } from "../../Redux/Reducers/redux-slice-settings-users";
import { cacheService } from "../../Core/Api/services";
import PageHead from "../../Components/Topbars/DashboardTopbar/page-head";
import LayoutCenterPanel from "../Dashboard-v2/layout-panel";
import { GetDrawerOpen, GetLeftPanelInfos, GetRightPanelInfos } from "../../Redux/Reducers/redux-slice-layout";
import SidePanelLayout from "../Dashboard-v2/side-panel-layout";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { useLocation, useNavigate } from "react-router";
import Logo from "../../Components/Logo/logo-svg";
import { OpenLinkInNewTab } from "../../Utils/helpers";
export interface UnauthDashboardLayoutProps {
  children?: React.ReactNode;
  ref?: React.ForwardedRef<HTMLDivElement>;
  withContainer?: boolean;
  maxWidth?: false | Breakpoint | undefined;
  showTopbarSearchOnScroll?: boolean;
  transparentTopbar?: boolean;
  sxContent?: SxProps<Theme>;
  setOpenSidebarFallback?: React.Dispatch<React.SetStateAction<boolean>>;
  noScroll?: boolean;
  pageTitle: string;
}

export class FilterContextClass {
  public openFiltersPanel: boolean = false;
  public openFiltersPanelLayer: ObjectLayer | null = ObjectLayer.Title;
  public openFiltersPanelType: MetadataFieldValueObjectTypes = MetadataFieldValueObjectTypes.DbAssetObjectInfos;
  public openFiltersPanelMetadataFields: DbMetadataFieldInfos[] | undefined = undefined;

  setOpenFiltersPanel(value: boolean): void {
    this.openFiltersPanel = value;
  }

  setOpenFiltersPanelLayer(value: ObjectLayer | null): void {
    this.openFiltersPanelLayer = value;
  }

  setOpenFiltersPanelType(value: MetadataFieldValueObjectTypes): void {
    this.openFiltersPanelType = value;
  }

  setOpenFiltersPanelMetadataFields(value: DbMetadataFieldInfos[] | undefined): void {
    this.openFiltersPanelMetadataFields = value;
  }
}

export const FilterContext = createContext(new FilterContextClass());

export const BasketContext = createContext({
  setOpenBasket: (open: boolean) => {},
  openBasket: false,
});

export const LayoutContext = createContext({
  setOpenSidebar: (open: boolean) => {},
  setVisibleBanner: (open: boolean) => {},
  openSidebar: true,
  visibleBanner: false,
});

export const minSliderWidthLeft = 150;
export const minSliderWidthRight = 225;
export const maxSliderWidth = 850;
export const pinnedSliderWidth = 16;

const UnauthDashboardLayout = forwardRef<HTMLDivElement | null, UnauthDashboardLayoutProps>(
  ({ children, withContainer = false, maxWidth = "xl", sxContent = {}, noScroll = false, pageTitle }, ref) => {
    const dispatch = useDispatch();

    const $content = React.useRef<HTMLDivElement>(undefined);
    const $scrollContent = React.useRef<HTMLDivElement>(undefined);
    const leftPanel = useSelector(GetLeftPanelInfos);
    const rightPanel = useSelector(GetRightPanelInfos);
    const drawerOpen = useSelector(GetDrawerOpen);
    const theme = useTheme();

    const toggleSidebar = () => {
      dispatch(ToggleSidebar());
    };

    const globalContainerRef = useRef<any>(undefined);

    return (
      <StyledPageWrapper ref={ref}>
        <Box
          ref={globalContainerRef}
          width={"100%"}
          height={"100vh"}
          display={"flex"}
          flexDirection={"column"}
          overflow={"hidden"}
          pb={1}
          borderRadius={1}
          sx={{
            transition: theme.transitions.create(["width"], {
              duration: theme.transitions.duration.short,
              easing: theme.transitions.easing.easeOut,
            }),
          }}
        >
          <Stack direction="row" justifyContent={"space-between"} alignItems={"center"} height={48} pr={2} pl={2.5}>
            <Typography variant="h4">{pageTitle}</Typography>
            <Stack
              gap={1}
              direction="row"
              alignItems="center"
              p={2}
              sx={{
                width: "auto",
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ flexShrink: 0 }}>
                Powered by
              </Typography>
              <Logo
                small={false}
                color={theme.palette.mode === "dark" ? "white" : "black"}
                onClick={() => OpenLinkInNewTab(GlobalConst.URL_WWW)}
              />
            </Stack>
          </Stack>
          <Stack
            display={"flex"}
            width={"100%"}
            height={"calc(100vh - 48px)"}
            flexDirection={"row"}
            padding={"8px"}
            paddingTop={0}
            paddingBottom={"0px"}
            borderRadius={"12px 24px"}
            overflow={"hidden"}
            sx={{
              ...(drawerOpen && {
                filter: "blur(6px)",
                transform: "scale(0.95)",
              }),
              transition: theme.transitions.create(["transform"], {
                duration: theme.transitions.duration.short,
                easing: theme.transitions.easing.easeOut,
              }),
            }}
          >
            <SidePanelLayout panel={leftPanel} position="left" />
            <LayoutCenterPanel
              children={children}
              withContainer={withContainer}
              maxWidth={maxWidth}
              sxContent={sxContent}
              leftPanel={leftPanel}
              rightPanel={rightPanel}
              noScroll={noScroll}
            />
            <SidePanelLayout panel={rightPanel} position="right" />
          </Stack>
        </Box>
      </StyledPageWrapper>
    );
  }
);

const StyledPageWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  overflowX: "hidden",
  background: theme.palette.background.page,
}));

export default UnauthDashboardLayout;
